import { useDebouncedState } from "@mantine/hooks";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Task } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { useCardDialog } from "~/components/autopto/cardDialog/useCardDialog";
import { isAutoptoCardRequired } from "~/components/autopto/isAutoptoTrial";
import Button from "~/components/mantine/button/Button";
import { AlertCard } from "~/components/ui/card/AlertCard";
import DataTable from "~/components/ui/dataTable/DataTable";
import {
  Pagination,
  PaginationConfig,
} from "~/components/ui/pagination/Pagination";
import { useTeam } from "~/contexts/TeamProvider";
import { useEndpointAccess } from "~/hooks/useEndpointAccess";
import { routes } from "~/router";

type InternalLinkStyles = {
  tableLink: string;
  favicon: string;
};

type PageStyles = {
  page: string;
  title: string;
  description: string;
  statusBadge: string;
  statusTodo: string;
  statusDoing: string;
  statusDone: string;
  statusCancelled: string;
} & InternalLinkStyles;

type LinksTasksPageProps = {
  title: string;
  description: string;
  siteId?: string;
  taskType: "BrandLink" | "NicheLink" | "OutreachLink";
  columns: ColumnDef<Task>[];
  searchRansackKey?: string;
  // Ransack sort fragment (e.g. "reddit_first ASC") always applied as the
  // primary key, ahead of the user's column sorting.
  primarySort?: string;
  // Optional CTA shown top-right, linking to the matching Done-For-You service
  // to drive traffic to the sales page.
  dfyLink?: { label: string; to: string };
  styles: PageStyles;
};

export const InternalLinkCell = ({
  to,
  domain,
  children,
  styles,
}: {
  to: string;
  domain: string;
  children: React.ReactNode;
  styles: InternalLinkStyles;
}) => (
  <Link to={to} className={styles.tableLink}>
    {domain && (
      <img
        src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`}
        alt=""
        className={styles.favicon}
        onError={(e) => {
          (e.target as HTMLImageElement).src =
            "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' width='32' height='32'%3E%3Cpath fill='%23ccc' d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z'/%3E%3C/svg%3E";
        }}
      />
    )}
    <div>{children}</div>
  </Link>
);

const STATUS_CLASS_MAP: Record<string, keyof PageStyles> = {
  todo: "statusTodo",
  doing: "statusDoing",
  done: "statusDone",
  cancelled: "statusCancelled",
  canceled: "statusCancelled",
};

export const StatusBadge = ({
  status,
  styles,
}: {
  status: string;
  styles: PageStyles;
}) => {
  const key = STATUS_CLASS_MAP[status.toLowerCase()] ?? "statusTodo";

  return (
    <span className={`${styles.statusBadge} ${styles[key]}`}>{status}</span>
  );
};

export const formatPercent = (value: number | null) =>
  value === null ? "0%" : `${Math.round(value)}%`;

const LinksTasksPage = ({
  title,
  description,
  siteId,
  taskType,
  columns,
  searchRansackKey,
  primarySort,
  dfyLink,
  styles,
}: LinksTasksPageProps) => {
  const navigate = useNavigate();
  // The Done-For-You offer leads to Services, closed to some teams. During
  // an Autopto trial without a card, Services lead the admin to the card
  // dialog.
  const { team } = useTeam();
  const { canAddCard } = useCardDialog();
  const canOpenServices =
    useEndpointAccess("orders", !!dfyLink) ||
    (isAutoptoCardRequired(team) && canAddCard);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useDebouncedState(
    "",
    500,
  );

  // Keep the default order in the fallback sort string, NOT in this state — the
  // table uses multi-sort (isMultiSortEvent), so a seeded default would stick
  // and never clear. Empty = "no user sort", which lets primarySort apply and
  // come back when the user clears their column sort. Pages without primarySort
  // seed probability so its sort indicator shows on load.
  const [sorting, setSorting] = useState<SortingState>(
    primarySort ? [] : [{ id: "probability", desc: true }],
  );

  const [pagination, setPagination] = useState<PaginationConfig>({
    currentPage: 1,
    itemsPerPage: 10,
    nextPage: null,
    prevPage: null,
    totalPages: 1,
    totalItems: 0,
  });

  useEffect(() => {
    const normalized = searchValue.replace(/\s+/g, " ").trim();
    setDebouncedSearchValue(normalized);
  }, [searchValue, setDebouncedSearchValue]);

  // Reset current page on page size change, search change, or sorting change.
  useEffect(() => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, [pagination.itemsPerPage, debouncedSearchValue, sorting]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      `${taskType}s`,
      siteId,
      pagination.currentPage,
      pagination.itemsPerPage,
      sorting,
      debouncedSearchValue,
    ],
    queryFn: async () => {
      const userSort =
        sorting.length > 0
          ? sorting.map((s) => `${s.id} ${s.desc ? "DESC" : "ASC"}`).join(", ")
          : "probability DESC";
      // Force primarySort only when there's no active user column sort; clearing
      // the sort empties `sorting` and brings it back.
      const sortString =
        primarySort && sorting.length === 0
          ? `${primarySort}, ${userSort}`
          : userSort;

      const q: Record<string, string> = { type_eq: taskType };
      if (debouncedSearchValue && searchRansackKey) {
        q[searchRansackKey] = debouncedSearchValue;
      }

      const response = await apiClient.sites.tasksDetail(Number(siteId), {
        s: sortString,
        "page[number]": pagination.currentPage,
        "page[size]": pagination.itemsPerPage,
        q,
      });

      const { headers } = response;

      setPagination((prev) => ({
        ...prev,
        totalItems: Number(headers.get("x-pager-total-count") || 0),
        nextPage: Number(headers.get("x-pager-next-page") || 0),
        prevPage: Number(headers.get("x-pager-prev-page") || 0),
        totalPages: Number(headers.get("x-pager-total-pages") || 0),
      }));

      return response.data?.tasks || [];
    },
    enabled: !!siteId,
    placeholderData: keepPreviousData,
  });

  const tasks = useMemo(() => data || [], [data]);

  // We can't surface link opportunities until the site has keywords. When the
  // list is empty AND the site has no keywords, prompt the user to add some.
  // Fetched in PARALLEL with the tasks (shared queryKey + staleTime, so it's
  // cached and effectively hits the network once per site across the link
  // pages). Gating this on an empty list instead would create a tasks→keywords
  // waterfall that visibly flashes loading → empty → alert.
  const { data: keywordCount } = useQuery({
    queryKey: ["site-keyword-count", siteId],
    queryFn: async () => {
      const response = await apiClient.sites.keywordsDetail(Number(siteId), {
        "page[size]": 1,
      });

      return Number(response.headers.get("x-pager-total-count") || 0);
    },
    enabled: !!siteId,
    staleTime: 5 * 60 * 1000,
  });

  // Three exclusive states for the list area:
  //  • the site has no keywords             → onboarding prompt
  //  • there are rows OR an active search   → the results table (it owns its
  //    own empty / "no matches" / pagination / search UI, so the search box
  //    stays available even when a query returns nothing)
  //  • still loading / no opportunities yet → render nothing
  const noKeywords = keywordCount === 0;
  const showTable = !isLoading && (tasks.length > 0 || !!debouncedSearchValue);

  return (
    <div className={styles.page}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "var(--lg)",
        }}
      >
        <div>
          <h1 className={styles.title}>{title}</h1>
          <p className={styles.description}>{description}</p>
        </div>
        {dfyLink && canOpenServices && (
          <Button onClick={() => navigate(dfyLink.to)}>{dfyLink.label}</Button>
        )}
      </div>

      {noKeywords ? (
        <AlertCard
          type="info"
          withIcon
          title="Add a Keyword to Get Your Link Opportunities"
          content={
            <>
              It looks like you haven&apos;t added any keywords yet. In order
              for us to deliver your link and citation recommendations, we need
              to know which keywords we want to use first 🙂
            </>
          }
          actions={
            <Button
              onClick={() =>
                navigate(routes.site.keywords.list({ id: String(siteId) }))
              }
            >
              Add Keywords
            </Button>
          }
        />
      ) : showTable ? (
        <>
          <DataTable
            columns={columns}
            data={tasks}
            isLoading={isLoading}
            disableSkeleton
            emptyText="No matching results."
            tableLayout="auto"
            search={{
              value: searchValue,
              onChange: setSearchValue,
              placeholder: "Search...",
            }}
            externalSorting={{
              state: sorting,
              onChange: setSorting,
            }}
          />

          <Pagination
            currentPage={pagination.currentPage}
            itemsPerPage={pagination.itemsPerPage}
            totalItems={pagination.totalItems}
            isLoading={isFetching}
            onPageChange={(page) =>
              setPagination((prev) => ({ ...prev, currentPage: page }))
            }
            onItemsPerPageChange={(items) =>
              setPagination((prev) => ({
                ...prev,
                itemsPerPage: items,
                currentPage: 1,
              }))
            }
          />
        </>
      ) : null}
    </div>
  );
};

export default LinksTasksPage;
